import React, { useState, useEffect } from "react";
import {
  Search,
  Eye,
  Calendar,
  LayoutGrid,
  List,
  ArrowUpDown,
  CheckCircle2,
  FileText,
  ChevronRight
} from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { getAllPayslipsApi, getPayslipApi, getEmployeesApi, FILE_BASE_URL } from "../../app/api";
import PayslipModal from "./components/PayslipModal";
import { toast } from "react-hot-toast";

const EmployeeAvatar = ({ employee, sizeClass = "w-8 h-8", textClass = "text-[12px]" }) => {
  const imageUrl = getProfileImageUrl(employee);
  const initial = (employee.name || "U")[0]?.toUpperCase() || "U";

  return (
    <div className={`${sizeClass} rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center ${textClass} font-bold uppercase overflow-hidden border border-white/10 shrink-0`}>
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={`${employee.name || "Employee"} profile`}
          className="w-full h-full object-cover"
          onError={(event) => {
            event.currentTarget.style.display = "none";
            const fallback = event.currentTarget.nextElementSibling;
            if (fallback) fallback.style.display = "flex";
          }}
        />
      ) : null}
      <span className={`${imageUrl ? "hidden" : "flex"} w-full h-full items-center justify-center`}>
        {initial}
      </span>
    </div>
  );
};

const normalizeFlag = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  const normalized = String(value).trim().toLowerCase();
  if (["true", "1", "yes", "y", "active"].includes(normalized)) return true;
  if (["false", "0", "no", "n", "inactive"].includes(normalized)) return false;
  return null;
};

const getNestedValue = (obj, keys) => {
  for (const key of keys) {
    const value = key.split(".").reduce((current, part) => current?.[part], obj);
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return "";
};

const getEmployeeName = (employee = {}) =>
  String(
    employee.name ||
    employee.employee_name ||
    employee.full_name ||
    `${employee.first_name || ""} ${employee.last_name || ""}`.trim()
  ).trim();

const getIdentityValues = (employee = {}) => [
  employee.id,
  employee.user_id,
  employee.employee_id,
  employee.emp_id,
  employee.roll_number,
  employee.roll_no,
  employee.employee_code,
  employee.emp_code
].filter((value) => value !== undefined && value !== null && String(value).trim() !== "");

const isCurrentEmployee = (employee = {}) => {
  const status = String(getNestedValue(employee, [
    "employment_status",
    "employee_status",
    "user_status",
    "status",
    "user.status",
    "employee.status"
  ])).trim().toLowerCase();

  const activeFlag = normalizeFlag(getNestedValue(employee, [
    "is_active",
    "active",
    "isActive",
    "user.is_active",
    "employee.is_active"
  ]));

  const deletedFlag = normalizeFlag(getNestedValue(employee, [
    "is_deleted",
    "deleted",
    "isDeleted",
    "user.is_deleted",
    "employee.is_deleted"
  ]));

  const exitDate = getNestedValue(employee, [
    "deleted_at",
    "exit_date",
    "left_at",
    "leaving_date",
    "last_working_day",
    "resigned_at",
    "terminated_at",
    "relieved_at",
    "user.deleted_at",
    "employee.deleted_at"
  ]);

  const inactiveStatuses = ["inactive", "deleted", "ex", "ex employee", "ex-employee", "former", "left", "resigned", "terminated", "relieved", "archived"];

  if (activeFlag === false || deletedFlag === true || exitDate) return false;
  if (inactiveStatuses.includes(status)) return false;
  return true;
};

const buildEmployeeLookup = (users = []) => {
  const lookup = new Map();
  users.filter(isCurrentEmployee).forEach((user) => {
    getIdentityValues(user).forEach((value) => lookup.set(String(value), user));
    const name = getEmployeeName(user).toLowerCase();
    if (name) lookup.set(`name:${name}`, user);
  });
  return lookup;
};

const findRealEmployee = (payslip, lookup) => {
  for (const value of getIdentityValues(payslip)) {
    const match = lookup.get(String(value));
    if (match) return match;
  }

  const name = getEmployeeName(payslip).toLowerCase();
  return name ? lookup.get(`name:${name}`) : null;
};

const mergePayslipsWithRealEmployees = (payslips = [], users = []) => {
  const lookup = buildEmployeeLookup(users);
  return payslips.reduce((realRows, payslip) => {
    const matchedUser = findRealEmployee(payslip, lookup);
    if (!matchedUser) return realRows;

    realRows.push({
      ...matchedUser,
      ...payslip,
      name: payslip.name || getEmployeeName(matchedUser),
      profile_pic: payslip.profile_pic || matchedUser.profile_pic,
      user_id: payslip.user_id || matchedUser.id || matchedUser.user_id
    });
    return realRows;
  }, []);
};

const getProfileImageUrl = (employee = {}) => {
  const path = getNestedValue(employee, ["profile_pic", "profilePic", "profile_image", "user.profile_pic", "employee.profile_pic"]);
  if (!path) return "";
  const cleanPath = String(path).trim();
  if (/^https?:\/\//i.test(cleanPath)) return cleanPath;
  return `${FILE_BASE_URL}${cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`}`;
};

const Payslips = () => {
  const [loading, setLoading] = useState(true);
  const [payslips, setPayslips] = useState([]);
  const [viewType, setViewType] = useState("list");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterMonth, setFilterMonth] = useState(moment().subtract(1, 'month').format("YYYY-MM"));
  const [isFetchingFullData, setIsFetchingFullData] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchPayslips();
  }, []);

  const fetchPayslips = async () => {
    try {
      setLoading(true);
      const [res, usersRes] = await Promise.all([
        getAllPayslipsApi(),
        getEmployeesApi()
      ]);
      if (res.success) {
        const rows = res.data || [];
        setPayslips(usersRes.success ? mergePayslipsWithRealEmployees(rows, usersRes.data || []) : rows.filter(isCurrentEmployee));
      } else {
        toast.error(res.message || "Failed to fetch payslips");
      }
    } catch (error) {
      console.error("Error fetching payslips:", error);
      toast.error("An error occurred while fetching payslips");
    } finally {
      setLoading(false);
    }
  };

  const handleViewPayslip = async (payslipObj) => {
    try {
      setIsFetchingFullData(true);
      // payslipObj.month is "YYYY-MM"
      const dateObj = moment(payslipObj.month, "YYYY-MM");
      const monthName = dateObj.format("MMMM");
      const yearStr = dateObj.format("YYYY");

      const res = await getPayslipApi(payslipObj.user_id, monthName, yearStr);
      if (res.success) {
        setSelectedPayslip(res.data);
        setIsModalOpen(true);
      } else {
        toast.error("Failed to load detailed payslip data");
      }
    } catch (error) {
      console.error("Error fetching full payslip:", error);
      toast.error("Error loading payslip details");
    } finally {
      setIsFetchingFullData(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedPayslips = [...payslips].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const filteredPayslips = sortedPayslips.filter(rec => {
    const matchesSearch = (rec.name || "").toLowerCase().includes(searchTerm.toLowerCase()) || (rec.user_id || "").toString().includes(searchTerm);
    const matchesMonth = filterMonth ? rec.month === filterMonth : true;
    return matchesSearch && matchesMonth;
  });

  const totalPages = Math.ceil(filteredPayslips.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPayslips = filteredPayslips.slice(indexOfFirstItem, indexOfLastItem);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterMonth]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#12082F] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#12082F] text-white p-4 sm:p-6 font-sans">
      <div className="max-w-[1600px] mx-auto space-y-6">

        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2 text-white">
              ALL PAYSLIPS <span className="text-white/70 font-normal">| {payslips.length} Generated</span>
            </h1>
            <p className="text-white/80 text-sm">View and download generated employee payslips</p>
          </div>
        </div>

        {/* CONTROLS */}
        <div className="bg-white/5 border border-white/10 p-4 rounded-2xl flex flex-col xl:flex-row items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row w-full xl:w-auto gap-4">
            {/* Search Input */}
            <div className="relative w-full sm:w-80">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
              <input
                type="text"
                placeholder="Search by name or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-purple-500/50 transition"
              />
            </div>

            {/* Month Filter */}
            <div className="flex items-center gap-2">
              <div className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 flex items-center gap-3 w-full sm:w-auto transition focus-within:border-purple-500/50">
                <Calendar size={16} className="text-white/70" />
                <DatePicker
                  selected={filterMonth ? new Date(filterMonth + "-01") : null}
                  onChange={(date) => {
                    setFilterMonth(date ? moment(date).format("YYYY-MM") : "");
                  }}
                  dateFormat="MMMM yyyy"
                  showMonthYearPicker
                  maxDate={moment().subtract(1, 'month').endOf('month').toDate()}
                  placeholderText="Select month..."
                  className="bg-transparent border-none text-sm text-white focus:outline-none cursor-pointer placeholder-white/40 w-32"
                />
              </div>
              {filterMonth && (
                <button
                  onClick={() => setFilterMonth("")}
                  className="text-white/70 hover:text-white/90 px-3 py-2 text-sm bg-white/5 hover:bg-white/10 rounded-xl border border-white/5 transition"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 w-full xl:w-auto justify-end">
            <div className="flex bg-white/5 border border-white/10 rounded-lg p-1">
              <button
                onClick={() => setViewType("list")}
                className={`p-2 rounded-md transition ${viewType === "list" ? "bg-purple-600 text-white" : "text-white/70 hover:text-white"}`}
              >
                <List size={18} />
              </button>
              <button
                onClick={() => setViewType("grid")}
                className={`p-2 rounded-md transition ${viewType === "grid" ? "bg-purple-600 text-white" : "text-white/70 hover:text-white"}`}
              >
                <LayoutGrid size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* LIST VIEW */}
        {viewType === "list" ? (
          <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[1000px]">
              <thead>
                <tr className="border-b border-white/10 bg-white/5">
                  <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider cursor-pointer group" onClick={() => handleSort('user_id')}>
                    <div className="flex items-center gap-2">ID <ArrowUpDown size={12} className="opacity-0 group-hover:opacity-100 transition" /></div>
                  </th>
                  <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider cursor-pointer group" onClick={() => handleSort('name')}>
                    <div className="flex items-center gap-2">Employee <ArrowUpDown size={12} className="opacity-0 group-hover:opacity-100 transition" /></div>
                  </th>
                  <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-right cursor-pointer group" onClick={() => handleSort('month')}>
                    <div className="flex items-center justify-end gap-2">Month <ArrowUpDown size={12} className="opacity-0 group-hover:opacity-100 transition" /></div>
                  </th>
                  <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-right">Net Salary</th>
                  <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-center">Status</th>
                  <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {currentPayslips.map((rec) => (
                  <tr key={rec.id} className="hover:bg-white/5 transition group">
                    <td className="p-4 text-sm text-white/60">VT-{String(rec.user_id).padStart(3, '0')}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <EmployeeAvatar employee={rec} />
                        <div>
                          <p className="text-sm font-semibold text-white">{rec.name}</p>
                          <p className="text-[10px] text-white/70">Generated: {new Date(rec.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-sm text-right font-semibold text-white/80">{rec.month}</td>
                    <td className="p-4 text-right">
                      <span className="text-sm font-black text-green-400">₹{Number(rec.net_salary).toLocaleString()}</span>
                    </td>
                    <td className="p-4 text-center">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${rec.status === 'approved' || rec.status === 'paid' ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                        }`}>
                        <CheckCircle2 size={10} />
                        {rec.status}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleViewPayslip(rec)}
                          disabled={isFetchingFullData}
                          className="px-3 py-1.5 bg-purple-600/20 hover:bg-purple-600/40 border border-purple-500/30 text-purple-400 rounded-lg text-xs font-bold flex items-center gap-2 transition disabled:opacity-50"
                          title="View & Download Payslip"
                        >
                          {isFetchingFullData ? (
                            <span className="w-3 h-3 border-2 border-purple-400/30 border-t-purple-400 rounded-full animate-spin" />
                          ) : (
                            <FileText size={14} />
                          )}
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          /* GRID VIEW */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {currentPayslips.map((rec) => (
              <div key={rec.id} className="bg-white/5 border border-white/10 p-5 rounded-2xl hover:border-purple-500/50 transition space-y-4 group">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <EmployeeAvatar employee={rec} sizeClass="w-10 h-10" textClass="text-sm" />
                    <div>
                      <h4 className="text-sm font-bold text-white">{rec.name}</h4>
                      <p className="text-[10px] text-white/70">ID: VT-{String(rec.user_id).padStart(3, '0')}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest ${rec.status === 'approved' || rec.status === 'paid' ? 'bg-green-500/20 text-green-400 border border-green-500/20' : 'bg-blue-500/20 text-blue-400 border border-blue-500/20'
                    }`}>
                    {rec.status}
                  </span>
                </div>

                <div className="space-y-2 py-3 border-y border-white/5">
                  <DataRow label="Month" value={rec.month} />
                  <DataRow label="Generated On" value={new Date(rec.created_at).toLocaleDateString()} />
                  <DataRow label="Gross Salary" value={`₹${Number(rec.gross_salary).toLocaleString()}`} />
                </div>

                <div className="flex items-center justify-between pt-1">
                  <div>
                    <p className="text-[10px] text-white/30 uppercase font-bold tracking-wider">Net Payable</p>
                    <p className="text-xl font-black text-green-400">₹{Number(rec.net_salary).toLocaleString()}</p>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleViewPayslip(rec)}
                      disabled={isFetchingFullData}
                      className="bg-purple-600/20 text-purple-400 hover:bg-purple-600/40 px-3 py-2 rounded-xl transition border border-purple-500/30 flex items-center gap-2 text-xs font-bold disabled:opacity-50"
                    >
                      {isFetchingFullData ? (
                        <span className="w-3 h-3 border-2 border-purple-400/30 border-t-purple-400 rounded-full animate-spin" />
                      ) : (
                        <Eye size={14} />
                      )}
                      View
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* PAGINATION */}
        {filteredPayslips.length > itemsPerPage && (
          <div className="flex items-center justify-center gap-2 py-6">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="p-2 text-white/70 hover:text-white transition disabled:opacity-30"
            >
              <ChevronRight size={18} className="rotate-180" />
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-8 h-8 rounded-lg text-sm font-bold transition ${currentPage === page
                  ? 'bg-purple-600 text-white'
                  : 'bg-white/5 text-white/60 hover:bg-white/10'
                  }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="p-2 text-white/70 hover:text-white transition disabled:opacity-30"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}

        {filteredPayslips.length === 0 && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-12 text-center">
            <div className="bg-white/5 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText size={32} className="text-white/20" />
            </div>
            <h3 className="text-lg font-bold text-white">No payslips found</h3>
            <p className="text-white/70 text-sm mt-1">Try a different search term or run payroll to generate payslips.</p>
          </div>
        )}

        <PayslipModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          data={selectedPayslip}
        />
      </div>
    </div>
  );
};

const DataRow = ({ label, value, color = "text-white" }) => (
  <div className="flex justify-between items-center text-[11px]">
    <span className="text-white/70 font-medium">{label}</span>
    <span className={`${color} font-bold`}>{value}</span>
  </div>
);

export default Payslips;
